import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { sendError } from '../../../../configuration/lang/services';
import { accessToken } from '../../../../configuration/tokenHandling/accessToken';
import type { Product, ViewOnlyProduct } from '../../checkout/redux/types';
import type { TermedProductInfo } from '../../common/termedLevel/redux/types';
import { isServiceVisible } from '../../common/utils/isServiceVisible';
import { getMarketplaceBackend } from '../../config/pathURL/config';
import { decodeJson, transformRtkQueryError } from '../api';
import { mapProductType } from '../mapProductType';
import { ApiProductCodec, ApiProductsCodec } from './product.types';
import { mapContractProductDetails, mapProduct } from './productMapper';

const transformResponse =
    <T>(mapResponse: (response: unknown) => T) =>
    (response: unknown): T => {
        try {
            return mapResponse(response);
        } catch (error) {
            sendError(error);
            throw error;
        }
    };

export const productApiSlice = createApi({
    reducerPath: 'productApi',
    baseQuery: fetchBaseQuery({
        baseUrl: getMarketplaceBackend(),
        prepareHeaders: headers => {
            const token = accessToken.getAccessToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    endpoints: builder => ({
        getAllProductDetails: builder.query<Array<ViewOnlyProduct & { legacy: boolean }>, void>({
            query: () => '/products',
            transformResponse: transformResponse(response =>
                decodeJson(ApiProductsCodec)(response)
                    .products.filter(product => isServiceVisible({ sku: product.id }))
                    .map(({ name, id, legacy, product_type }) => ({
                        sku: id,
                        name,
                        legacy,
                        productType: mapProductType(product_type),
                    }))
            ),
            transformErrorResponse: response => transformRtkQueryError(response, [401]),
        }),
        getAllNonLegacyProducts: builder.query<Product[], void>({
            query: () => '/products',
            transformResponse: transformResponse(response =>
                decodeJson(ApiProductsCodec)(response)
                    .products.filter(product => isServiceVisible({ sku: product.id }))
                    .filter(product => !product.legacy)
                    .map(mapProduct)
            ),
            transformErrorResponse: response => transformRtkQueryError(response, [401]),
        }),
        getProductDetails: builder.query<Product, string>({
            query: productId => `/products/${encodeURIComponent(productId)}`,
            transformResponse: transformResponse(response => mapProduct(decodeJson(ApiProductCodec)(response))),
            transformErrorResponse: response => transformRtkQueryError(response, [401]),
        }),
        getContractProductDetails: builder.query<TermedProductInfo, string>({
            query: productId => `/products/${encodeURIComponent(productId)}`,
            transformResponse: transformResponse(response =>
                mapContractProductDetails(decodeJson(ApiProductCodec)(response))
            ),
            transformErrorResponse: response => transformRtkQueryError(response, [401]),
        }),
    }),
});

export const {
    useGetAllProductDetailsQuery,
    useGetAllNonLegacyProductsQuery,
    useGetProductDetailsQuery,
    useGetContractProductDetailsQuery,
} = productApiSlice;
